import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { StorageService } from './services/storage.service';
import { Config } from './config';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { TokenRefresh } from './interfaces/auth/token-refresh.interface';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppInjector } from './services/app-injector.service';

@Injectable()
export class APIInterceptor implements HttpInterceptor {
  refreshInProgress: boolean = false;
  showSessionEndMessage: boolean = true;
  messageCounter: number = 0;
  tokenTurnSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  translate: TranslateService;

  constructor(
    public storage: StorageService,
    public router: Router,
    public route: ActivatedRoute,
    public http: HttpClient,
    public toast: ToastrService
  ) {
    const injector = AppInjector.getInjector();
    this.translate = injector.get(TranslateService);
  }

  correctNavigationLang() {
    let res = navigator.language;
    const resSplitFloor = res.split('_');
    const resSplitDash = res.split('-');

    if (resSplitFloor.length > 1) {
      res = resSplitFloor[0];
    }
    if (resSplitDash.length > 1) {
      res = resSplitDash[0];
    }
    res = this.translate.getLangs().indexOf(res) > -1 ? res : 'en';
    return res.toLowerCase();
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    // change url on server  url + add token if exist
    let formatUrl = { url: req.url };
    const paths = ['/project/create', '/help-ukraine-request/create'];

    if (
      (this.storage.get(StorageService.SESSION_COOKIE) &&
        Config.URL_WITHOUT_AUTHORIZATION.indexOf(req.url.split('/api/')[1]) === -1) ||
      paths.indexOf(window.location.pathname) > -1
    ) {
      formatUrl = Object.assign(formatUrl, {
        setHeaders: {
          Authorization: 'Bearer ' + this.storage.get(StorageService.SESSION_COOKIE),
          'Accept-Language': this.storage.get('lang'),
        },
      });
    } else {
      let lang = this.storage.get('lang')
        ? this.storage.get('lang')
        : this.correctNavigationLang().toLowerCase();
      formatUrl = Object.assign(formatUrl, { setHeaders: { 'Accept-Language': lang } });
    }
    req = req.clone(formatUrl);

    return next.handle(req).pipe(
      catchError((err: HttpErrorResponse) => {
        switch (err?.error?.message) {
          case 'jwt_token_not_found': {
            this.storage.logout();
            if (this.showSessionEndMessage) {
              this.toast.error(
                this.translate.instant('AUTH.session_out'),
                this.translate.instant('AUTH.session_out_title')
              );
              this.showSessionEndMessage = false;
            }
          }
          case 'expired_jwt_token': {
            this.storage.delete(StorageService.SESSION_COOKIE); // delete token from localStorage -> delete from headers
            return !this.refreshInProgress
              ? this.refreshToken(req, next)
              : this.tokenTurnContinue(req, next);
          }
          case 'refresh_token_expired': {
            this.storage.logout();
            if (this.showSessionEndMessage) {
              this.toast.error(
                this.translate.instant('AUTH.session_out'),
                this.translate.instant('AUTH.session_out_title')
              );
              this.showSessionEndMessage = false;
            }
            break;
          }
          case 'Invalid JWT Token': {
            this.storage.logout();
            if (this.showSessionEndMessage) {
              this.toast.error(
                this.translate.instant('AUTH.session_out'),
                this.translate.instant('AUTH.session_out_title')
              );
              this.showSessionEndMessage = false;
            }
            break;
          }
          case 'account_is_blocked_by_administration': {
            this.storage.logout(false);
            this.storage.put('account_blocked', 'true');
            this.toast.error(
              this.translate.instant('AUTH.account_is_blocked_by_administration'),
              this.translate.instant('AUTH.session_out_title')
            );
            break;
          }
          case 'unauthorized': {
            this.storage.logout(false);
            const currentParams = this.route.snapshot.queryParams;
            this.router.navigate([], { queryParams: { ...currentParams, auth: 'login' } });
            // this.toast.error(this.translate.instant('AUTH.session_out'), this.translate.instant('AUTH.session_out_title'));
            break;
          }
          default: {
            return throwError(err);
          }
        }

        if (err.status === 500) {
          this.toast.error(
            this.translate.instant('AUTH.server_problem'),
            this.translate.instant('AUTH.unexpected_error')
          );
        }
      })
    );
  }

  get countSimilarErrors() {
    return this.messageCounter++;
  }

  refreshToken(req: HttpRequest<any>, next: HttpHandler) {
    this.tokenTurnSubject.next(null);
    this.refreshInProgress = true;
    return this.http
      .post(Config.API_URL + '/token/refresh', {
        refresh_token: this.storage.get(StorageService.SESSION_REFRESH_COOKIE),
      })
      .pipe(
        switchMap((data: TokenRefresh) => {
          this.storage.setToken(data); // have token
          this.refreshInProgress = false;
          this.tokenTurnSubject.next(data.token); // add token to subject stream (other requests now can use him)
          req = req.clone({
            setHeaders: {
              Authorization: 'Bearer ' + this.storage.get(StorageService.SESSION_COOKIE),
            },
          });
          return next.handle(req);
        })
      );
  }

  tokenTurnContinue(req: HttpRequest<any>, next: HttpHandler) {
    return this.tokenTurnSubject // wait on stream event
      .pipe(
        filter((token) => token != null),
        take(1), // check what you have in tokenTurnSubject
        switchMap(() => {
          req = req.clone({
            setHeaders: {
              Authorization: 'Bearer ' + this.storage.get(StorageService.SESSION_COOKIE),
            },
          });
          return next.handle(req);
        })
      );
  }
}
